import { Link, useLocation } from 'react-router-dom'
import { clearSessionAndRedirectToLogin } from '../../config/api'
import { useEffect, useMemo, useState } from 'react'
import {
  LayoutDashboard,
  Calendar,
  Coffee,
  Users,
  Briefcase,
  BarChart2,
  Settings,
  LogOut,
  Menu,
  X,
  ExternalLink,
  PartyPopper,
} from 'lucide-react'

interface DashboardLayoutProps {
  children: React.ReactNode
}

const menuCategories = [
  {
    title: 'Übersicht',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    ],
  },
  {
    title: 'Inhalte',
    items: [
      { icon: Calendar, label: 'Events', path: '/dashboard/events' },
      { icon: Coffee, label: 'Brunch', path: '/dashboard/brunches' },
      { icon: Briefcase, label: 'Stellen', path: '/dashboard/bewerbung' },
    ],
  },
  {
    title: 'Auswertung',
    items: [
      { icon: BarChart2, label: 'Statistiken', path: '/dashboard/statistik' },
      { icon: PartyPopper, label: 'Events & Partys', path: '/dashboard/statistik/partys' },
      { icon: Coffee, label: 'Brunch-Stats', path: '/dashboard/statistik/brunch' },
    ],
  },
  {
    title: 'System',
    items: [
      { icon: Users, label: 'Benutzer', path: '/dashboard/users' },
      { icon: Settings, label: 'Einstellungen', path: '/dashboard/settings' },
    ],
  },
]

function isActivePath(pathname: string, path: string) {
  if (path === '/dashboard') return pathname === '/dashboard'
  return pathname === path
}

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation()

  return (
    <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-5">
      {menuCategories.map((category) => (
        <div key={category.title}>
          <p className="px-2 mb-1.5 text-[11px] font-medium uppercase tracking-wider text-zinc-500">
            {category.title}
          </p>
          <div className="space-y-0.5">
            {category.items.map((item) => {
              const Icon = item.icon
              const active = isActivePath(location.pathname, item.path)
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onNavigate}
                  className={`flex items-center gap-2.5 px-2.5 h-9 rounded-md text-sm transition-colors ${
                    active
                      ? 'bg-brand-800 text-white'
                      : 'text-zinc-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{item.label}</span>
                </Link>
              )
            })}
          </div>
        </div>
      ))}
    </nav>
  )
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const location = useLocation()
  const [currentUser, setCurrentUser] = useState<{ name?: string; email?: string } | null>(null)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  useEffect(() => {
    const userStr = localStorage.getItem('dashboard_user')
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr))
      } catch (e) {
        console.error('Error parsing user data:', e)
      }
    }
  }, [])

  useEffect(() => {
    setIsSidebarOpen(false)
  }, [location.pathname])

  const pageTitle = useMemo(() => {
    const all = menuCategories.flatMap((c) => c.items)
    return all.find((item) => isActivePath(location.pathname, item.path))?.label ?? 'Dashboard'
  }, [location.pathname])

  const handleLogout = () => {
    clearSessionAndRedirectToLogin()
  }

  const userInitial = (currentUser?.name || currentUser?.email || 'A').charAt(0).toUpperCase()

  return (
    <div className="dashboard-shell flex h-[100dvh] bg-gray-950 text-zinc-200">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-gray-800 bg-gray-950">
        <div className="h-14 shrink-0 flex items-center gap-2.5 px-4 border-b border-gray-800">
          <img src="/Logo_HM.png" alt="Hückels May" className="h-8 w-auto" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate leading-tight">Hückels May</p>
            <p className="text-[11px] text-zinc-500 truncate">Admin</p>
          </div>
        </div>

        <SidebarNav />

        <div className="shrink-0 border-t border-gray-800 p-3">
          {currentUser && (
            <div className="flex items-center gap-2.5 px-1 mb-2">
              <div className="h-8 w-8 rounded-full bg-brand-800 text-white text-xs font-semibold flex items-center justify-center shrink-0">
                {userInitial}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{currentUser.name || 'Admin'}</p>
                <p className="text-[11px] text-zinc-500 truncate">{currentUser.email}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full h-9 px-2.5 rounded-md text-sm text-zinc-400 hover:text-white hover:bg-white/5"
          >
            <LogOut className="h-4 w-4" />
            Abmelden
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <button
            className="absolute inset-0 bg-black/60"
            aria-label="Menü schließen"
            onClick={() => setIsSidebarOpen(false)}
          />
          <aside className="relative h-full w-72 max-w-[85vw] flex flex-col bg-gray-950 border-r border-gray-800">
            <div className="h-14 shrink-0 flex items-center justify-between px-4 border-b border-gray-800">
              <div className="flex items-center gap-2.5">
                <img src="/Logo_HM.png" alt="Hückels May" className="h-8 w-auto" />
                <span className="text-sm font-semibold text-white">Admin</span>
              </div>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="p-2 rounded-md text-zinc-400 hover:text-white hover:bg-white/5"
                aria-label="Schließen"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <SidebarNav onNavigate={() => setIsSidebarOpen(false)} />
            <div className="shrink-0 border-t border-gray-800 p-3">
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full h-9 px-2.5 rounded-md text-sm text-zinc-400 hover:text-white hover:bg-white/5"
              >
                <LogOut className="h-4 w-4" />
                Abmelden
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col bg-gray-900">
        <header className="h-14 shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 border-b border-gray-800 bg-gray-950">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden p-2 -ml-2 rounded-md text-zinc-300 hover:bg-white/5"
              aria-label="Menü öffnen"
            >
              <Menu className="h-5 w-5" />
            </button>
            <span className="lg:hidden text-sm font-medium text-white truncate">{pageTitle}</span>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Zur Website</span>
          </Link>
        </header>

        <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 py-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
